import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Link, Text } from '@antimatterhq/ui/components/typography';
import Spacer from '@antimatterhq/ui/components/spacer';
import ModifyRiskScoreDialog from '@/components/dialog/modifyRiskScore/ModifyRiskScoreDialog';
import {
  BaseObservablesTable,
  ObservableItem,
} from '@/components/observables/table/BaseObservablesTable';
import { useClearCacheObservablesKind } from '@/hooks/dbui/useGetObservablesKind';

export type ObservablesTop10Props = {
  name: string;
  items?: ObservableItem[];
  onModifyRiskScore: ({
    value,
    riskScore,
  }: {
    value: string;
    riskScore: number;
  }) => void;
};

export const ObservablesTop10 = ({
  name,
  items,
  onModifyRiskScore,
}: ObservablesTop10Props) => {
  const navigate = useNavigate();
  const [editItem, setEditItem] = useState<
    { kind: string; value: string; riskScore: number } | undefined
  >(undefined);
  const clearListCache = useClearCacheObservablesKind();

  const modifyRiskScore = useCallback(
    async ({
      value,
      riskScore,
      kind,
    }: {
      value: string;
      riskScore: number;
      kind: string;
    }) => {
      onModifyRiskScore({ value, riskScore });
      setEditItem(undefined);
      clearListCache({ kind });
    },
    [onModifyRiskScore, clearListCache],
  );

  const handleEdit = (item: ObservableItem) => {
    setEditItem({
      kind: name,
      value: item.value || '',
      riskScore: item.riskScore || 0,
    });
  };

  return (
    <div>
      <div className="flex">
        <div className="flex-grow">
          <Text size="lg">{`Top 10 ${name} by Risk`}</Text>
        </div>
        <div>
          <Link to={`/explore/observables/${name}`} className="text-sm">
            See All
          </Link>
        </div>
      </div>

      <Spacer size="base" />

      <BaseObservablesTable
        name={name}
        items={items}
        maxRowCount={10}
        showActions
        onEdit={handleEdit}
        onView={(item) => {
          navigate(
            `/explore/observables/${name}/${item.value}/events?previousPage=observables`,
          );
        }}
      />

      <ModifyRiskScoreDialog
        kind={editItem?.kind || ''}
        isOpen={!!editItem}
        onCancel={() => setEditItem(undefined)}
        value={editItem?.value || ''}
        riskScore={editItem?.riskScore || 0}
        onSave={(riskScore) =>
          modifyRiskScore({
            value: editItem?.value || '',
            riskScore,
            kind: editItem?.kind || '',
          })
        }
      />
    </div>
  );
};
